import { useMemo } from 'react';
import { useCareProgramsData } from './useCareProgramsData';
import { RECOMMENDED_CARE_PROGRAM_NAMES } from '../constants';
import type { Program } from '../types';

interface RecommendedCareProgramsData {
  recommendedPrograms: Program[];
  isLoading: boolean;
  error: unknown;
}

/**
 * Hook to get recommended/featured care programs in priority order.
 * Returns up to 6 programs that are available for the current hospital,
 * ordered by the recommended priority list. Filters out programs the user is already enrolled in.
 */
export const useRecommendedCarePrograms = (): RecommendedCareProgramsData => {
  const { groupedProgramsByTopicMap, isLoading, error } = useCareProgramsData();

  const recommendedPrograms = useMemo(() => {
    const allPrograms = Object.values(groupedProgramsByTopicMap).flatMap((sections) =>
      sections.flatMap((section) => section.programs),
    );
    const programMap = new Map(allPrograms.map((program) => [program.title.trim(), program]));
    return RECOMMENDED_CARE_PROGRAM_NAMES.map((programName) => programMap.get(programName.trim()))
      .filter(
        (program): program is Program =>
          program !== undefined && program.enrollmentStatus !== 'enrolled',
      )
      .slice(0, 5);
  }, [groupedProgramsByTopicMap]);

  return {
    recommendedPrograms,
    isLoading,
    error,
  };
};
